const REQUIRED_COLUMNS = ["ID", "Caratteristiche", "Stato", "Data di registrazione"];

export function decodeTradetrackerCsv(buffer) {
  const bytes = new Uint8Array(buffer);
  const encoding = bytes[0] === 0xff && bytes[1] === 0xfe
    ? "utf-16le"
    : bytes[0] === 0xfe && bytes[1] === 0xff ? "utf-16be" : "utf-8";
  return new TextDecoder(encoding, { fatal: true }).decode(buffer).replace(/^\uFEFF/, "");
}

// Conserva la riga fisica di inizio record anche con celle multilinea e righe vuote.
function readRecords(text) {
  const records = [];
  let cells = [], cell = "", quoted = false, closed = false;
  let line = 1, startLine = 1;
  const finishCell = () => { cells.push(cell); cell = ""; closed = false; };
  const finishRecord = () => {
    finishCell();
    if (cells.some(value => value.trim() !== "")) records.push({ cells, line: startLine });
    cells = [];
  };
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char === '"') {
      if (quoted && text[i + 1] === '"') { cell += '"'; i += 1; }
      else if (quoted) { quoted = false; closed = true; }
      else if (!cell && !closed) quoted = true;
      else throw new Error(`Virgolette non valide alla riga ${line}`);
    } else if ((char === "\r" || char === "\n")) {
      const newline = char === "\r" && text[i + 1] === "\n" ? "\r\n" : char;
      if (newline.length === 2) i += 1;
      if (quoted) cell += newline;
      else finishRecord();
      line += 1;
      if (!quoted) startLine = line;
    } else if (char === "," && !quoted) finishCell();
    else {
      if (closed) throw new Error(`Caratteri dopo le virgolette alla riga ${line}`);
      cell += char;
    }
  }
  if (quoted) throw new Error(`Virgolette non chiuse alla riga ${startLine}`);
  finishRecord();
  return records;
}

export function normalizzaImporto(value) {
  const text = String(value || "").replace(/[\s€]/g, "");
  if (!text) return null;
  if (!/^-?(?:\d+|\d{1,3}(?:\.\d{3})+)(?:,\d{1,2})?$/.test(text)) {
    throw new Error(`Importo non valido: ${value}`);
  }
  return Number(text.replace(/\./g, "").replace(",", "."));
}

export function normalizzaData(value) {
  const text = String(value || "").trim();
  if (!text) return null;
  const match = /^(\d{2})\/(\d{2})\/(\d{2}),\s*(\d{2}):(\d{2}):(\d{2})$/.exec(text);
  if (!match) throw new Error(`Data non valida: ${value}`);
  const [, day, month, year, hour, minute, second] = match;
  const date = new Date(Date.UTC(2000 + Number(year), Number(month) - 1, Number(day)));
  if (date.getUTCFullYear() !== 2000 + Number(year) || date.getUTCMonth() !== Number(month) - 1 ||
      date.getUTCDate() !== Number(day) || Number(hour) > 23 || Number(minute) > 59 || Number(second) > 59) {
    throw new Error(`Data non valida: ${value}`);
  }
  return `20${year}-${month}-${day} ${hour}:${minute}:${second}`;
}

export function parseTradetrackerCsv(buffer) {
  const records = readRecords(decodeTradetrackerCsv(buffer));
  const headers = (records.shift()?.cells || []).map(header => header.trim());
  const missing = REQUIRED_COLUMNS.filter(header => !headers.includes(header));
  if (missing.length) throw new Error(`Colonne mancanti: ${missing.join(", ")}`);
  if (new Set(headers).size !== headers.length) throw new Error("Il CSV contiene colonne duplicate");
  if (!records.length) throw new Error("Il CSV non contiene righe dati");
  if (records.length > 5000) throw new Error("Sono consentite al massimo 5000 righe per file");
  return records.map(({ cells, line }, index) => {
    try {
      if (cells.length !== headers.length) throw new Error("Numero di colonne diverso dall'intestazione");
      const raw = Object.fromEntries(headers.map((header, i) => [header, cells[i]]));
      const idOrdinePs = raw.Caratteristiche.trim();
      if (!idOrdinePs) throw new Error("Caratteristiche: id ordine Prestashop mancante");
      return {
        progressivoRiga: index,
        numeroRiga: line,
        idTransazioneTt: raw.ID.trim().replace(/^#/, ""),
        idOrdinePs,
        statoTt: raw.Stato,
        sitoAffiliato: raw["Sito affiliato"] || null,
        importoOrdine: normalizzaImporto(raw["Importo d'ord."]),
        commissione: normalizzaImporto(raw.Costi),
        dataRegistrazione: normalizzaData(raw["Data di registrazione"]),
        dataValidazione: normalizzaData(raw["Data di validazione"]),
        dataClick: normalizzaData(raw["Data del clic di origine"]),
        paese: raw.Paese || null,
        raw,
      };
    } catch (error) {
      throw new Error(`Riga ${line}: ${error.message}`);
    }
  });
}

export function getTradetrackerDateRange(rows) {
  const dates = rows.map(row => row.dataRegistrazione?.slice(0, 10)).filter(Boolean).sort();
  return { periodoDa: dates[0] || null, periodoA: dates[dates.length - 1] || null };
}
